import type { Citation } from "@op/puck-types";
import { Dc_Reading, Dc_Text_Citation } from "@op/sf-op";
import { CitationSourceLink } from "../../../../../components/base/CitationSourceLink";
import { makeCitationId } from "@op/utils";
import { useCallback, useEffect, useRef, useState } from "react";

export interface ReadingCompanionCitationsProps {
  citations: Citation[];
}

const ReadingCompanionCitationItem = ({ citation }: { citation: Citation }) => {
  const observer = useRef<IntersectionObserver | null>(null);
  const [isScrolledPast, setIsScrolledPast] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const onCitationClick = useCallback(() => {
    const citationId = makeCitationId(citation.index);
    const citationElement = document.getElementsByClassName(citationId)[0];
    if (citationElement instanceof HTMLElement) {
      citationElement.scrollIntoView({ behavior: "smooth" });
    }
  }, [citation.index]);

  function handleIntersection(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        setIsActive(true);
        setIsScrolledPast(false);
      } else {
        setIsActive(false);
        setIsScrolledPast(entry.boundingClientRect.top < 0);
      }
    });
  }

  useEffect(() => {
    const citationId = makeCitationId(citation.index);
    const target = document.getElementsByClassName(citationId)[0];
    if (!(target instanceof HTMLElement)) {
      console.warn(`Target element with class name ${citationId} not found`);
      return;
    }

    observer.current = new IntersectionObserver(handleIntersection, {
      rootMargin: "20% 0px 20% 0px",
      threshold: 1.0,
    });
    observer.current.observe(target);

    return () => {
      if (observer.current) {
        observer.current.disconnect();
        observer.current = null;
      }
    };
  }, []);

  return (
    <Dc_Reading.ReferenceItem
      linkSlot={<CitationSourceLink citation={citation} />}
      onClick={() => onCitationClick()}
    >
      <Dc_Reading.ReferenceItemIndex>
        [{citation.index}]
      </Dc_Reading.ReferenceItemIndex>
      <Dc_Text_Citation.Text
        variant={
          isActive ? "active" : isScrolledPast ? "scrolled-past" : "inactive"
        }
      >
        {citation.author}, {citation.content}
      </Dc_Text_Citation.Text>
    </Dc_Reading.ReferenceItem>
  );
};

export const ReadingCompanionCitations = ({
  citations,
}: ReadingCompanionCitationsProps) => {
  return (
    <Dc_Reading.ReferenceItemStack>
      {citations.map((citation) => (
        <ReadingCompanionCitationItem key={citation.id} citation={citation} />
      ))}
    </Dc_Reading.ReferenceItemStack>
  );
};
